import React, { useState, useCallback } from 'react';
import { Row, Col, Card, Badge, Button, Table, Spinner, Alert } from 'react-bootstrap';
import { InfoRow } from './InfrastructureSectionPanel';
import apiService from '../../services/api';

const InfrastructureHealthPanel = ({ data }) => {
    const [showErrors, setShowErrors] = useState(false);
    const [errors, setErrors] = useState(null);
    const [errorsLoading, setErrorsLoading] = useState(false);
    const [errorsError, setErrorsError] = useState('');
    const [expandedRow, setExpandedRow] = useState(null);

    const fetchErrors = useCallback(async () => {
        setErrorsLoading(true);
        setErrorsError('');
        const res = await apiService.getTechnicalApplication();
        if (res.success) setErrors(res.data?.recentErrors || []);
        else setErrorsError(res.error || 'Failed to load application errors');
        setErrorsLoading(false);
    }, []);

    const handleToggleErrors = () => {
        const next = !showErrors;
        setShowErrors(next);
        if (next && errors === null && !errorsLoading) fetchErrors();
    };

    return (
        <Row className="g-3">
            <Col xs={12}>
                <div className="d-flex align-items-center gap-2 mb-3">
                    <Badge bg={data.status === 'ok' ? 'success' : 'danger'} className="fs-6 px-3 py-2">
                        <i className={`bi bi-${data.status === 'ok' ? 'check-circle' : 'x-circle'} me-1`}></i>
                        {data.status?.toUpperCase()}
                    </Badge>
                    <span className="text-muted small">{data.timestamp}</span>
                </div>
            </Col>
            <Col md={6}>
                <Card className="border-0 bg-light">
                    <Card.Body className="py-2 px-3">
                        <div className="small fw-semibold mb-2 text-secondary">Process</div>
                        <InfoRow label="Uptime" value={data.uptime} />
                        <InfoRow label="Heap Used" value={data.memory?.heapUsedPercent} />
                        <InfoRow label="RSS" value={data.memory?.rss} />
                    </Card.Body>
                </Card>
            </Col>
            <Col md={6}>
                <Card className="border-0 bg-light">
                    <Card.Body className="py-2 px-3">
                        <div className="small fw-semibold mb-2 text-secondary">System</div>
                        <InfoRow label="Load Avg (1m)" value={data.cpu?.loadAvg1m} />
                        <InfoRow label="Free Memory" value={data.os?.freeMemPercent} />
                        <InfoRow label="Event Loop p99" value={data.eventLoop?.p99DelayMs != null ? `${data.eventLoop.p99DelayMs} ms` : null} />
                    </Card.Body>
                </Card>
            </Col>
            <Col md={12}>
                <Card className="border-0 bg-light">
                    <Card.Body className="py-2 px-3">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <div className="small fw-semibold text-secondary">Requests</div>
                            <Button
                                variant={showErrors ? 'danger' : 'outline-danger'}
                                size="sm"
                                onClick={handleToggleErrors}
                            >
                                <i className={`bi bi-${showErrors ? 'eye-slash' : 'exclamation-triangle'} me-1`}></i>
                                {showErrors ? 'Hide' : 'Show'} Recent Errors
                                {errors && errors.length > 0 && (
                                    <Badge bg="light" text="dark" className="ms-2">{errors.length}</Badge>
                                )}
                            </Button>
                        </div>
                        <div className="d-flex flex-wrap gap-3">
                            {data.requests && Object.entries(data.requests).map(([k, v]) => (
                                <div key={k} className="text-center">
                                    <div className="fw-bold">{v}</div>
                                    <div className="text-muted" style={{ fontSize: '0.7rem' }}>{k}</div>
                                </div>
                            ))}
                        </div>
                    </Card.Body>
                </Card>
            </Col>
            {showErrors && (
                <Col md={12}>
                    <Card className="border-0 bg-light">
                        <Card.Body className="py-2 px-3">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <div className="small fw-semibold text-secondary">
                                    <i className="bi bi-exclamation-triangle me-1"></i>Recent Errors
                                </div>
                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    onClick={fetchErrors}
                                    disabled={errorsLoading}
                                >
                                    <i className="bi bi-arrow-clockwise me-1"></i>Refresh
                                </Button>
                            </div>
                            {errorsLoading && (
                                <div className="text-center py-3">
                                    <Spinner animation="border" size="sm" /> <span className="ms-2 text-muted small">Loading errors...</span>
                                </div>
                            )}
                            {!errorsLoading && errorsError && (
                                <Alert variant="danger" className="py-2 mb-0 small">{errorsError}</Alert>
                            )}
                            {!errorsLoading && !errorsError && errors && errors.length === 0 && (
                                <div className="text-center text-muted small py-3">
                                    <i className="bi bi-check-circle me-1"></i>No recent errors
                                </div>
                            )}
                            {!errorsLoading && !errorsError && errors && errors.length > 0 && (
                                <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                    <Table size="sm" hover responsive className="mb-0">
                                        <thead>
                                            <tr>
                                                <th style={{ width: '180px' }}>Timestamp</th>
                                                <th>Message</th>
                                                <th style={{ width: '80px' }}>Stack</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {errors.slice().reverse().map((err, idx) => (
                                                <React.Fragment key={idx}>
                                                    <tr>
                                                        <td className="text-muted small">
                                                            {err.timestamp ? new Date(err.timestamp).toLocaleString() : '-'}
                                                        </td>
                                                        <td className="small">{err.message || '-'}</td>
                                                        <td>
                                                            {err.stack && (
                                                                <Button
                                                                    variant="link"
                                                                    size="sm"
                                                                    className="p-0"
                                                                    onClick={() => setExpandedRow(expandedRow === idx ? null : idx)}
                                                                >
                                                                    {expandedRow === idx ? 'Hide' : 'View'}
                                                                </Button>
                                                            )}
                                                        </td>
                                                    </tr>
                                                    {expandedRow === idx && err.stack && (
                                                        <tr>
                                                            <td colSpan={3} className="bg-white">
                                                                <pre className="small mb-0" style={{ whiteSpace: 'pre-wrap', maxHeight: '200px', overflow: 'auto' }}>
                                                                    {err.stack}
                                                                </pre>
                                                            </td>
                                                        </tr>
                                                    )}
                                                </React.Fragment>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            )}
        </Row>
    );
};

export default InfrastructureHealthPanel;
